/**
 * The Conveyor opencode events plugin — the trusted structured-event source
 * for the interactive opencode TUI.
 *
 * opencode's client engine loads config-declared plugins and awaits their
 * `event` hook for every bus event (verified live on 1.18.4 AND 1.18.15 with a
 * real tool turn: every `message.part.updated` — text, step-start,
 * tool pending→running→completed, step-finish — plus the session.status/idle
 * lifecycle arrived in the hook). The plugin appends each event as one NDJSON
 * line to the sink file named by CONVEYOR_OPENCODE_EVENTS_FILE, and the PTY
 * session tails that file exactly the way it tails Claude's transcript JSONL.
 *
 * The source is EMBEDDED as a string and written to the session temp dir at
 * spawn time rather than shipped as a dist asset: it versions with this file,
 * needs no tsup asset plumbing, and the `file://` config specifier it is
 * loaded through was probe-verified. Design notes:
 *   - `message.part.delta` is skipped IN the plugin — the hook is awaited per
 *     event, so per-token deltas would be pure sink bloat.
 *   - A missing sink env makes the plugin a no-op instead of throwing: a
 *     plugin error inside opencode must never take down a human's session.
 *   - appendFileSync keeps event ordering without an async queue; events are
 *     small and local, and the same call pattern was used by the live probe.
 */

import { writeFile } from "node:fs/promises";
import { join } from "node:path";

/** Child env var naming the NDJSON sink the plugin appends to. */
export const OPENCODE_EVENTS_FILE_ENV = "CONVEYOR_OPENCODE_EVENTS_FILE";

export const CONVEYOR_PLUGIN_SOURCE = `import { appendFileSync } from "node:fs";

export const ConveyorEventsPlugin = async () => ({
  event: async ({ event }) => {
    const sink = process.env.${OPENCODE_EVENTS_FILE_ENV};
    if (!sink || !event || event.type === "message.part.delta") return;
    try {
      appendFileSync(sink, JSON.stringify(event) + "\\n");
    } catch {
      // Never let a sink hiccup break the user's opencode session.
    }
  },
});
`;

/**
 * Write the plugin into `tempDir` and return its path plus the sink path the
 * spawned TUI's env must carry. The sink file itself is created empty so the
 * tailer has something to stat before the first event.
 */
export async function stageOpenCodePlugin(
  tempDir: string,
): Promise<{ pluginPath: string; eventsSinkPath: string }> {
  const pluginPath = join(tempDir, "conveyor-events-plugin.mjs");
  const eventsSinkPath = join(tempDir, "opencode-events.ndjson");
  await writeFile(pluginPath, CONVEYOR_PLUGIN_SOURCE, "utf8");
  await writeFile(eventsSinkPath, "", "utf8");
  return { pluginPath, eventsSinkPath };
}
